/**
 * @cogo/react-renderer-v2 — v2 renderer with pure TS runtime + minimal React shell.
 *
 * Re-uses v1 element renderers (48+) via @cogo/react-renderer.
 * Architecture: app-runtime.ts (pure TS) + App.tsx (React shell) + Block.tsx (render).
 */

// Components
export { AppV2 } from "./components/AppWithAgent"
export { COGO_RUNTIME_EVENT_EXECUTION_EVENT } from "./components/App"
export type { AppV2Props } from "./components/AppWithAgent"
export { BlockV2 } from "./components/Block"
export type { BlockV2Props } from "./components/Block"


// Runtime
export { createAppRuntime, getSharedRegistry, matchRoute, findContentSlot } from "./runtime/app-runtime"
export type { AppRuntime, AppRuntimeOptions, ThemeTokens } from "./runtime/app-runtime"
export { materializeBlocksFromRenderPackBytes } from "./runtime/render-pack-transport"
export type { AppRenderPackBytes } from "./runtime/render-pack-transport"
export { registerBridgeToolsV2 } from "./runtime/bridge-api"
export { EventDispatcherV2 } from "./runtime/event-dispatcher"

// Host services — back the four host-effect actions (emitEvent / openModal /
// invokeCallback / mediaControl) + cropImage. See docs/studio/actionflow-host-wiring.md.
export { createHostServices, wireHostHooks } from "./host/host-services"
export type { HostServices } from "./host/host-services"
export { MotionRegistry, keyframesForPreset } from "./host/motion-registry"
export type {
  MotionAnimationLike,
  MotionElementLike,
  MotionElementResolver,
  MotionResult,
  MotionStatus,
} from "./host/motion-registry"
export { AppEventBus } from "./host/event-bus"
export type { AppEventListener } from "./host/event-bus"
export { CallbackRegistry } from "./host/callback-registry"
export type { HostCallback } from "./host/callback-registry"
export { MediaRegistry, applyMediaCommand } from "./host/media-registry"
export type { MediaElementLike } from "./host/media-registry"
export { ModalHost, ModalStore } from "./host/modal-host"
export type { ModalState, ModalComponentProps, ModalComponentRegistry } from "./host/modal-host"
export { cropImageWeb, parseRatio } from "./host/crop-image-web"

// Core
export { RenderContextV2, useRenderContextV2 } from "./core/render-context"
export type { RenderContextV2Value } from "./core/render-context"

// Hooks
export { useBlockSymbols } from "./hooks"

// Block loader (re-export from runtime-v2)
export { loadBlock, isV2Block, V2_FILES } from "./core/block-loader"
export type { FileReader } from "./core/block-loader"

// Re-export v1 renderers for direct access
export { registerAllRenderers, ElementRegistry, globalRegistry } from "@cogo/react-renderer"

// Re-export v2 types for convenience
export type {
  App, Page, BlockLayout, BlockDef, BlockMeta,
  UiElement, UiTree, EventMap, EventBinding,
  Flow, FlowStep, FlowMap,
  BlockState, VariableDef,
  DataSource, DataMap,
  StateContract, InputSymbol, OutputSymbol,
  WebMCPConfig, WebMCPToolDef,
} from "@cogo/types-v2"

// Re-export v2 runtime for convenience
export { StateManager, ActionFlowEngine, StateContractEnforcer } from "@cogo/runtime-v2"
export type { PlatformAdapter, ToastHandler } from "@cogo/runtime-v2"

// Agent
export { CogoAgent } from "./components/agent/CogoAgent"
export type { CogoAgentProps } from "./components/agent/CogoAgent"

// Sandbox — AppSandbox lifecycle facade for IDE integration (Phase 152/153)
export { createAppSandbox } from './sandbox/app-sandbox.js'
export type {
  AppSandbox,
  AppSandboxOptions,
  AppSandboxState,
  SandboxModules,
  CreateAppSandbox,
} from './sandbox/app-sandbox.types.js'
